/**
 * SSR-safe wrappers for posthog-js/react hooks.
 * Returns no-op values on server to prevent module resolution errors.
 * With bootstrap data, flags are available immediately on first render.
 */

/**
 * SSR-safe wrapper for usePostHog
 * Returns null on server, actual posthog instance on client
 */
export const usePostHog = () => {
  if (typeof window === 'undefined') return null;
  return window.posthog || null;
};

/**
 * SSR-safe wrapper for useFeatureFlagEnabled.
 *
 * Always false on the server, where PostHog can't run. `posthog.init` happens in
 * entry-client before React hydrates, and bootstrap data comes with it, so the
 * client has the real answer on its first render.
 *
 * A caller that *denies* on a false flag (404, redirect) can't tell "off" from
 * "server" here, and would 404 an entitled user on every hard load. Such a caller
 * should check `usePostHog()` itself and hold a loading state while it's null —
 * see useSurveysAccess.
 */
export const useFeatureFlagEnabled = (flag) => {
  const posthog = usePostHog();
  return posthog?.isFeatureEnabled?.(flag) ?? false;
};
